import { Component, inject } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { DialogComponent } from '@app/modules/shared/components/dialog/dialog.component'
import { Registration } from '@database/models/registration.model'
import { NbDialogService } from '@nebular/theme'
import { take } from 'rxjs/operators'
import { CommentModalComponent } from '../comment-modal/comment-modal.component'

@Component({
  selector: 'app-flag-dropdown-panel',
  templateUrl: './flag-dropdown-panel.component.html',
  standalone: true
})
export class FlagDropdownPanelComponent {
  registrant!: Registration
  closeDropdown?: () => void

  private readonly _dialog = inject(MatDialog)
  private readonly _dialogService = inject(NbDialogService)

  /**
   * @description Set a colored flag on the registration and close the panel.
   * @param color Flag color to apply.
   * @returns void
   */
  public setFlag(color: 'red' | 'orange' | 'green'): void {
    this._closePanel()
    this.registrant.setColoredFlag(color)
  }

  /**
   * @description Set a colored flag and open the comment modal.
   * @param color Flag color to apply with the comment.
   * @returns void
   */
  public setFlagAndComment(color: 'red' | 'orange' | 'green'): void {
    const dialog = this._dialogService.open(CommentModalComponent, {
      context: {
        commentContent: '',
        color: color
      } as any,
      closeOnBackdropClick: true,
      autoFocus: false
    })

    this._closePanel()

    dialog.onClose.pipe(take(1)).subscribe((result) => {
      if (result && result.comment !== undefined) {
        this.registrant.setColoredFlag(color, result.comment || '')
      }
    })
  }

  /**
   * @description Remove the active flag from the registration.
   * @returns void
   */
  public removeFlag(): void {
    this._closePanel()
    this.registrant.removeFlag()
  }

  /**
   * @description Open a dialog listing existing flag comments.
   * @returns void
   */
  public viewComments(): void {
    this._closePanel()
    const commentsHtml = this._buildCommentsHtml()
    if (!commentsHtml) return

    this._dialog.open(DialogComponent, {
      minWidth: '320px',
      maxWidth: '400px',
      width: '90vw',
      panelClass: 'matdialog',
      data: {
        headerText: 'Flag Comments',
        cancelButtonText: 'Close',
        confirmButtonText: '',
        confirmButtonClass: '',
        hideIcon: true,
        messageText: commentsHtml,
        allowHtml: true
      }
    })
  }

  /**
   * @description Close the parent `app-dropdown` overlay.
   * @returns void
   */
  private _closePanel(): void {
    this.closeDropdown?.()
  }

  /**
   * @description Build HTML for flags that have a comment.
   * @returns Markup string, or empty when no comments exist.
   */
  private _buildCommentsHtml(): string {
    const flags = this.registrant.getFlags().filter((f) => f.comment && f.comment.trim().length > 0)
    if (flags.length === 0) return ''

    return flags
      .map((f) => {
        const flagColor = f.flag === 'red' ? '#d32f2f' : f.flag === 'orange' ? '#f57c00' : '#388e3c'
        const bgColor = f.flag === 'red' ? '#fee2e2' : f.flag === 'orange' ? '#fed7aa' : '#dcfce7'
        const escapedBy = f.by.replace(/</g, '&lt;').replace(/>/g, '&gt;')
        const timeStr = new Date(f.time).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })
        return `
        <div class="tw-mb-3 tw-flex tw-gap-2 tw-items-start">
          <div class="tw-flex-shrink-0 tw-mt-1">
            <div class="tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center" style="background-color: ${bgColor} !important;">
              <i class="fas fa-flag" style="color: ${flagColor} !important; font-size: 12px !important;"></i>
            </div>
          </div>
          <div class="tw-flex-1 tw-min-w-0">
            <div class="tw-rounded tw-px-3 tw-py-2 tw-mb-1 tw-bg-white tw-border" style="border-color: ${flagColor} !important; border-width: 1px;">
              <div class="tw-text-gray-900 tw-text-sm tw-leading-relaxed comment-content">${f.comment || ''}</div>
            </div>
            <div class="tw-text-xs tw-text-gray-500 tw-ml-1">
              <span class="tw-font-medium">${escapedBy}</span>
              <span class="tw-mx-1">•</span>
              <span>${timeStr}</span>
            </div>
          </div>
        </div>
      `
      })
      .join('')
  }
}
